import {
  isStopwatchSectionalDistanceMeters,
  type Sectional,
  type StopwatchRunSnapshot,
} from "@hisani/stopwatch";
import Dexie, { type Table } from "dexie";
import { z } from "zod";

const LEGACY_ACTIVE_SESSION_ID = "active-stopwatch-session";
const DEFAULT_HISTORY_PAGE_SIZE = 10;
export const STOPWATCH_COMMENT_MAX_LENGTH = 500;

const stopwatchCommentSchema = z
  .string()
  .trim()
  .max(STOPWATCH_COMMENT_MAX_LENGTH)
  .transform((comment) => (comment.length === 0 ? null : comment))
  .nullable();

const sectionalSchema = z.object({
  sequence: z.number().int().positive(),
  distanceMeters: z.number().refine(isStopwatchSectionalDistanceMeters),
  cumulativeMilliseconds: z.number().int().nonnegative(),
  splitMilliseconds: z.number().int().nonnegative(),
});

const persistedTimerSchema = z.object({
  comment: stopwatchCommentSchema.default(null),
  run: z.object({
    id: z.string().uuid(),
    label: z.string().nullable(),
    status: z.enum(["idle", "paused", "stopped"]),
    elapsedMilliseconds: z.number().int().nonnegative(),
    sectionals: z.array(sectionalSchema),
  }),
});

export interface PersistedTimerSnapshot {
  readonly comment: string | null;
  readonly run: StopwatchRunSnapshot;
}

export interface CompletedStopwatchSession {
  readonly id: string;
  readonly completedAtEpochMilliseconds: number;
  readonly timers: readonly PersistedTimerSnapshot[];
}

export interface StopwatchHistoryCursor {
  readonly completedAtEpochMilliseconds: number;
  readonly id: string;
}

export interface StopwatchHistoryPage {
  readonly sessions: readonly CompletedStopwatchSession[];
  readonly nextCursor: StopwatchHistoryCursor | null;
}

interface SessionRow {
  readonly id: string;
  readonly recordType: "active" | "completed";
  readonly updatedAtEpochMilliseconds: number;
  readonly completedAtEpochMilliseconds?: number;
}

interface RunRow {
  readonly id: string;
  readonly sessionId: string;
  readonly order: number;
  readonly label: string | null;
  readonly status: StopwatchRunSnapshot["status"];
  readonly elapsedMilliseconds: number;
  readonly comment: string | null;
}

interface SectionalRow extends Sectional {
  readonly id: string;
  readonly runId: string;
}

interface LegacyRunRow extends RunRow {
  readonly distanceValue?: string;
  readonly distanceUnit?: "FURLONGS" | "METERS";
}

interface LegacySectionalRow {
  readonly id: string;
  readonly runId: string;
  readonly sequence: number;
  readonly distanceMeters?: number;
  readonly cumulativeMilliseconds: number;
  readonly splitMilliseconds: number;
}

class StopwatchDatabase extends Dexie {
  public readonly sessions: Table<SessionRow, string>;
  public readonly runs: Table<RunRow, string>;
  public readonly sectionals: Table<SectionalRow, string>;

  public constructor() {
    super("hisani-stopwatch");
    this.version(1).stores({
      sessions: "id",
      runs: "id, sessionId, order",
      sectionals: "id, runId, sequence",
    });
    this.version(2).stores({
      sessions:
        "id, recordType, completedAtEpochMilliseconds, [completedAtEpochMilliseconds+id]",
      runs: "id, sessionId, order",
      sectionals: "id, runId, sequence",
    });
    this.version(3).stores({
      settings: "key",
    });
    this.version(4).stores({
      settings: null,
    });
    this.version(5)
      .stores({
        sessions:
          "id, recordType, completedAtEpochMilliseconds, [completedAtEpochMilliseconds+id]",
        runs: "id, sessionId, order",
        sectionals: "id, runId, sequence",
      })
      .upgrade(async (transaction) => {
        await transaction
          .table<SectionalRow, string>("sectionals")
          .where("id")
          .startsWith(`${LEGACY_ACTIVE_SESSION_ID}:`)
          .delete();
        await transaction
          .table<RunRow, string>("runs")
          .where("sessionId")
          .equals(LEGACY_ACTIVE_SESSION_ID)
          .delete();
        await transaction
          .table<SessionRow, string>("sessions")
          .delete(LEGACY_ACTIVE_SESSION_ID);
      });
    this.version(6)
      .stores({
        sessions:
          "id, recordType, completedAtEpochMilliseconds, [completedAtEpochMilliseconds+id]",
        runs: "id, sessionId, order",
        sectionals: "id, runId, sequence",
      })
      .upgrade(async (transaction) => {
        const runsTable = transaction.table<LegacyRunRow, string>("runs");
        const sectionalsTable = transaction.table<LegacySectionalRow, string>(
          "sectionals",
        );
        const runs = await runsTable.toArray();
        const runsById = new Map(runs.map((run) => [run.id, run]));
        const sectionals = await sectionalsTable.toArray();
        const migratedSectionals = sectionals.flatMap((sectional) => {
          const distanceMeters =
            sectional.distanceMeters ??
            legacyDistanceMeters(runsById.get(sectional.runId));
          if (distanceMeters === null) return [];
          return [{ ...sectional, distanceMeters }];
        });

        await sectionalsTable.bulkPut(migratedSectionals);
      });
    this.version(7)
      .stores({
        sessions:
          "id, recordType, completedAtEpochMilliseconds, [completedAtEpochMilliseconds+id]",
        runs: "id, sessionId, order",
        sectionals: "id, runId, sequence",
      })
      .upgrade(async (transaction) => {
        await transaction
          .table<RunRow, string>("runs")
          .toCollection()
          .modify({ comment: null });
      });
    this.sessions = this.table("sessions");
    this.runs = this.table("runs");
    this.sectionals = this.table("sectionals");
  }
}

let database: StopwatchDatabase | null = null;

export async function finishStopwatchSession(
  session: CompletedStopwatchSession,
): Promise<void> {
  const parsedSession = parseCompletedSession(session);
  const db = getDatabase();

  await db.transaction("rw", db.sessions, db.runs, db.sectionals, async () => {
    await replaceSessionTimers(db, parsedSession.id, parsedSession.timers);
    await db.sessions.put({
      id: parsedSession.id,
      recordType: "completed",
      updatedAtEpochMilliseconds: parsedSession.completedAtEpochMilliseconds,
      completedAtEpochMilliseconds: parsedSession.completedAtEpochMilliseconds,
    });
  });
}

export async function loadStopwatchHistory(
  cursor: StopwatchHistoryCursor | null = null,
  pageSize = DEFAULT_HISTORY_PAGE_SIZE,
): Promise<StopwatchHistoryPage> {
  const db = getDatabase();
  const boundedPageSize = Math.max(1, Math.min(50, Math.floor(pageSize)));
  const loadedSessions: {
    readonly row: SessionRow;
    readonly session: CompletedStopwatchSession;
  }[] = [];
  let scanCursor = cursor;
  let reachedEnd = false;

  while (loadedSessions.length <= boundedPageSize && !reachedEnd) {
    const collection =
      scanCursor === null
        ? db.sessions.orderBy("[completedAtEpochMilliseconds+id]").reverse()
        : db.sessions
            .where("[completedAtEpochMilliseconds+id]")
            .below([scanCursor.completedAtEpochMilliseconds, scanCursor.id])
            .reverse();
    const rows = await collection.limit(boundedPageSize + 1).toArray();
    reachedEnd = rows.length < boundedPageSize + 1;

    for (const row of rows) {
      const session = await loadCompletedSession(db, row).catch(
        (error: unknown) => {
          if (error instanceof z.ZodError) {
            return null;
          }
          throw error;
        },
      );
      if (session !== null) {
        loadedSessions.push({ row, session });
      }
      if (loadedSessions.length > boundedPageSize) {
        break;
      }
    }

    const lastScannedRow = rows.at(-1);
    if (lastScannedRow === undefined) {
      reachedEnd = true;
    } else {
      scanCursor = cursorForRow(lastScannedRow);
    }
  }

  const sessions = loadedSessions
    .slice(0, boundedPageSize)
    .map(({ session }) => session);
  const lastReturned = loadedSessions[boundedPageSize - 1];

  return {
    sessions,
    nextCursor:
      loadedSessions.length > boundedPageSize && lastReturned !== undefined
        ? cursorForRow(lastReturned.row)
        : null,
  };
}

async function loadCompletedSession(
  db: StopwatchDatabase,
  row: SessionRow,
): Promise<CompletedStopwatchSession> {
  return parseCompletedSession({
    id: row.id,
    completedAtEpochMilliseconds:
      row.completedAtEpochMilliseconds ?? Number.NaN,
    timers: await loadTimersForSession(db, row.id),
  });
}

function cursorForRow(row: SessionRow): StopwatchHistoryCursor {
  return {
    completedAtEpochMilliseconds:
      row.completedAtEpochMilliseconds ?? Number.NaN,
    id: row.id,
  };
}

export async function deleteStopwatchSession(sessionId: string): Promise<void> {
  const db = getDatabase();
  await db.transaction("rw", db.sessions, db.runs, db.sectionals, async () => {
    const session = await db.sessions.get(sessionId);
    if (session?.recordType !== "completed") {
      return;
    }
    await deleteSession(db, sessionId);
  });
}

export async function updateStopwatchTimerComment(
  sessionId: string,
  timerId: string,
  comment: string | null,
): Promise<string | null> {
  const parsedComment = stopwatchCommentSchema.parse(comment);
  const db = getDatabase();

  await db.transaction("rw", db.sessions, db.runs, async () => {
    const session = await db.sessions.get(sessionId);
    const run = await db.runs.get(timerId);
    if (session?.recordType !== "completed" || run?.sessionId !== sessionId) {
      throw new Error("The saved stopwatch could not be found.");
    }
    await db.runs.update(timerId, { comment: parsedComment });
  });

  return parsedComment;
}

export async function clearStopwatchStorage(): Promise<void> {
  const db = getDatabase();
  await db.transaction("rw", db.sessions, db.runs, db.sectionals, async () => {
    await db.sectionals.clear();
    await db.runs.clear();
    await db.sessions.clear();
  });
}

async function loadTimersForSession(
  db: StopwatchDatabase,
  sessionId: string,
): Promise<readonly PersistedTimerSnapshot[]> {
  const runs = await db.runs
    .where("sessionId")
    .equals(sessionId)
    .sortBy("order");
  if (runs.length === 0) {
    return [];
  }

  const sectionals = await db.sectionals
    .where("id")
    .startsWith(`${sessionId}:`)
    .toArray();
  const timers = runs.map((run) => ({
    comment: run.comment ?? null,
    run: {
      id: run.id,
      label: run.label,
      status: run.status,
      elapsedMilliseconds: run.elapsedMilliseconds,
      sectionals: sectionals
        .filter(({ runId }) => runId === run.id)
        .sort((left, right) => left.sequence - right.sequence)
        .map(
          ({
            sequence,
            distanceMeters,
            cumulativeMilliseconds,
            splitMilliseconds,
          }): Sectional => ({
            sequence,
            distanceMeters,
            cumulativeMilliseconds,
            splitMilliseconds,
          }),
        ),
    },
  }));

  return persistedTimerSchema.array().parse(timers);
}

async function replaceSessionTimers(
  db: StopwatchDatabase,
  sessionId: string,
  timers: readonly PersistedTimerSnapshot[],
): Promise<void> {
  const parsedTimers = persistedTimerSchema.array().parse(timers);
  await db.sectionals.where("id").startsWith(`${sessionId}:`).delete();
  await db.runs.where("sessionId").equals(sessionId).delete();

  await db.runs.bulkPut(
    parsedTimers.map(({ comment, run }, order) => ({
      id: run.id,
      sessionId,
      order,
      label: run.label,
      status: run.status,
      elapsedMilliseconds: run.elapsedMilliseconds,
      comment,
    })),
  );
  await db.sectionals.bulkPut(
    parsedTimers.flatMap(({ run }) =>
      run.sectionals.map((sectional) => ({
        id: `${sessionId}:${run.id}:${sectional.sequence}`,
        runId: run.id,
        ...sectional,
      })),
    ),
  );
}

function legacyDistanceMeters(run: LegacyRunRow | undefined): number | null {
  if (run?.distanceValue === undefined || run.distanceUnit === undefined) {
    return null;
  }
  const value = Number(run.distanceValue);
  const distanceMeters = run.distanceUnit === "FURLONGS" ? value * 200 : value;
  return isStopwatchSectionalDistanceMeters(distanceMeters)
    ? distanceMeters
    : null;
}

async function deleteSession(
  db: StopwatchDatabase,
  sessionId: string,
): Promise<void> {
  await db.sectionals.where("id").startsWith(`${sessionId}:`).delete();
  await db.runs.where("sessionId").equals(sessionId).delete();
  await db.sessions.delete(sessionId);
}

function parseCompletedSession(
  session: CompletedStopwatchSession,
): CompletedStopwatchSession {
  return z
    .object({
      id: z.string().uuid(),
      completedAtEpochMilliseconds: z.number().int().nonnegative(),
      timers: persistedTimerSchema
        .array()
        .min(1)
        .refine(
          (timers) => timers.every(({ run }) => run.status === "stopped"),
          "Completed sessions must contain only stopped runs.",
        ),
    })
    .parse(session);
}

function getDatabase(): StopwatchDatabase {
  if (typeof indexedDB === "undefined") {
    throw new Error("IndexedDB is unavailable.");
  }
  database ??= new StopwatchDatabase();
  return database;
}
